<template>
  <div v-if="visible" class="notice-detail-modal" role="dialog" aria-modal="true" @click.self="close">
    <div class="notice-detail-modal__dialog">
      <div class="notice-detail-modal__header">
        <div>
          <span class="notice-detail-modal__eyebrow">
            <i class="uil-megaphone"></i>
            {{ $t('general.announcement') }}
          </span>
          <h3>{{ notice.name || notice.title || $t('general.announcement') }}</h3>
        </div>
        <div class="notice-detail-modal__header-actions">
          <p v-if="notice.start_date && notice.end_date" class="notice-detail-modal__dates">
            <i class="uil-calendar-alt"></i>
            {{ formatDate(notice.start_date) }} – {{ formatDate(notice.end_date) }}
          </p>
          <button type="button" class="notice-detail-modal__close" :aria-label="$t('general.close')" @click="close">
            <i class="uil-times"></i>
          </button>
        </div>
      </div>

      <div class="notice-detail-modal__body">
        <div v-if="loading" class="notice-detail-modal__loading">
          <i class="uil-spinner-alt"></i>
        </div>
        <template v-else>
          <div class="notice-detail-modal__content" v-html="notice.text || notice.description || '-'" />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import ApiService from "@/core/services/api.service";

export default {
  name: "NoticeDetailModal",
  data() {
    return {
      visible: false,
      loading: false,
      notice: {},
    };
  },
  methods: {
    async open(item) {
      if (!item || !item.id) return;

      this.notice = item;
      this.visible = true;
      this.loading = true;
      document.body.classList.add("notice-modal-open");

      try {
        const response = await ApiService.get(`api/user/active-notices/${item.id}`);
        this.notice = response?.data?.data || response?.data || item;
      } catch (error) {
        // Liste cevabındaki içerik varsa modal yine kullanılabilir kalır.
        console.error("Notice detail load error:", error);
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.visible = false;
      document.body.classList.remove("notice-modal-open");
    },
    onKeydown(event) {
      if (event.key === "Escape" && this.visible) this.close();
    },
    formatDate(value) {
      return moment(value, "YYYY-MM-DD").locale(this.$i18n?.locale || "tr").format("D MMMM YYYY");
    },
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
    document.body.classList.remove("notice-modal-open");
  },
};
</script>

<style lang="scss" scoped>
.notice-detail-modal {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(3px);

  &__dialog {
    width: min(680px, 100%);
    max-height: min(80vh, 760px);
    overflow: hidden;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.25);
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    padding: 22px 24px 18px;
    border-bottom: 1px solid #e2e8f0;

    h3 {
      margin: 5px 0 0;
      color: #0f172a;
      font-size: 22px;
      line-height: 1.3;
    }
  }

  &__eyebrow {
    color: #2563eb;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  &__close {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
    flex: 0 0 44px;
    border: 0;
    border-radius: 12px;
    background: #f1f5f9;
    color: #475569;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover,
    &:focus-visible {
      background: #e2e8f0;
      color: #0f172a;
    }

    &:focus-visible {
      outline: 2px solid var(--backgroundColor, #666CFF);
      outline-offset: 2px;
    }
  }

  &__body {
    max-height: calc(80vh - 100px);
    overflow-y: auto;
    padding: 22px 24px 28px;
  }

  &__dates {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin: 0;
    padding: 4px 8px;
    border-radius: 6px;
    background: #eff6ff;
    color: #1d4ed8;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.25;
    white-space: nowrap;

    i {
      font-size: 12px;
    }
  }

  &__content {
    color: #334155;
    font-size: 16px;
    line-height: 1.6;
    overflow-wrap: anywhere;

    :deep(p) {
      margin: 0 0 16px;
    }

    :deep(p:last-child) {
      margin-bottom: 0;
    }

    :deep(img),
    :deep(video),
    :deep(iframe) {
      display: block;
      max-width: 100%;
      height: auto;
    }

    :deep(a) {
      overflow-wrap: anywhere;
    }
  }

  &__loading {
    padding: 40px;
    color: #2563eb;
    font-size: 28px;
    text-align: center;

    i { display: inline-block; animation: notice-spin 0.8s linear infinite; }
  }
}

@keyframes notice-spin { to { transform: rotate(360deg); } }

@media (max-width: 576px) {
  .notice-detail-modal {
    align-items: center;
    padding: 12px;
  }

  .notice-detail-modal__dialog {
    width: min(680px, 100%);
    max-height: 88vh;
    border-radius: 16px;
  }

  .notice-detail-modal__header,
  .notice-detail-modal__body {
    padding-left: 16px;
    padding-right: 16px;
  }

  .notice-detail-modal__header {
    flex-direction: column;
    gap: 12px;
    padding-top: 20px;
    padding-bottom: 16px;
  }

  .notice-detail-modal__header-actions {
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }

  .notice-detail-modal__header h3 {
    font-size: 20px;
    line-height: 1.3;
  }

  .notice-detail-modal__close {
    width: 44px;
    height: 44px;
    flex-basis: 44px;
    min-width: 44px;
    min-height: 44px;
  }

  .notice-detail-modal__dates {
    min-width: 0;
    max-width: calc(100% - 56px);
    padding: 6px 8px;
    font-size: 12px;
    line-height: 1.4;
    white-space: normal;
  }

  .notice-detail-modal__content {
    font-size: 16px;
    line-height: 1.5;
  }

  .notice-detail-modal__body {
    max-height: calc(88vh - 150px);
    padding-top: 18px;
    padding-bottom: calc(24px + env(safe-area-inset-bottom));
  }
}
</style>

<style>
body.notice-modal-open { overflow: hidden; }
</style>
